import React, { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  InputLabel,
  DialogTitle,
  Dialog,
  Select,
  Box,
  MenuItem,
  FormControl,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; // Import useTheme for spacing
import { useDispatch } from "react-redux";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { addRecipe } from "../actions/recipes";
import { Close } from "@mui/icons-material";

// Note: Removed 'makeStyles' and '@mui/styles' imports

function AddRecipe(props) {
  const { onClose, open } = props;
  const theme = useTheme(); // Access the theme directly for spacing

  // --- Recipe State ---
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [servings, setServings] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [cookTime, setCookTime] = useState("");
  const [image, setImage] = useState("");

  // --- Ingredient State ---
  const [name, setName] = useState("");
  const [amount, setAmount] = useState(0);
  const [measurement, setMeasurement] = useState("");
  const [ingredients, setIngredients] = useState([]);

  // --- Direction State ---
  const [instruction, setInstruction] = useState("");
  const [optional, setOptional] = useState("");
  const [directions, setDirections] = useState([]); // Corrected typo from setDriections

  const dispatch = useDispatch();

  // --- Handlers ---
  const handleClose = () => {
    // Reset all states upon closing
    setTitle("");
    setDescription("");
    setServings("");
    setPrepTime("");
    setCookTime("");
    setImage("");
    setName("");
    setAmount(0);
    setMeasurement("");
    setInstruction("");
    setOptional("");
    setIngredients([]);
    setDirections([]);
    onClose();
  };

  const handleOptionsChange = (e) => setOptional(e.target.value);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleDescriptionChange = (e) => setDescription(e.target.value);
  const handleServingsChange = (e) => setServings(e.target.value);
  const handlePrepTimeChange = (e) => setPrepTime(e.target.value);
  const handleCookTimeChange = (e) => setCookTime(e.target.value);
  const handleNameChange = (e) => setName(e.target.value);
  const handleAmountChange = (e) => setAmount(e.target.value);
  const handleMeasurementChange = (e) => setMeasurement(e.target.value);
  const handleInstructionChange = (e) => setInstruction(e.target.value);
  const handleImageChange = (e) => setImage(e.target.value);

  // --- Add Ingredient ---
  function addIngredient() {
    if (!name || !amount || !measurement) return; // Simple validation

    const newIngredient = {
      uuid: uuidv4(),
      name,
      amount: parseFloat(amount),
      measurement,
    };

    setIngredients((prev) => [...prev, newIngredient]);
    setName("");
    setAmount(0);
    setMeasurement("");
  }

  // --- Add Direction ---
  function addDirection() {
    if (!instruction) return; // Simple validation

    const newDirection = {
      instruction,
      optional: optional === true ? "Yes" : "No",
    };

    setDirections((prev) => [...prev, newDirection]);
    setInstruction("");
    setOptional(false); // Reset to No/false
  }

  // --- Final Recipe Submission ---
  function addFoodItem() {
    if (!title || directions.length === 0 || ingredients.length === 0) {
      alert("Please fill in the Title and add at least one Ingredient and Direction.");
      return;
    }

    const dateAndTime = moment().format("L LTS");

    const newRecipe = {
      uuid: uuidv4(),
      title,
      description,
      servings,
      cookTime,
      prepTime,
      ingredients,
      directions,
      postDate: dateAndTime,
      images: !!image ? { medium: image } : null,
    };

    dispatch(addRecipe(newRecipe));
    handleClose();
  }

  // Define a style object for consistent input appearance using the theme
  const inputStyle = { mb: 2, width: '100%' };

  // Define the close button style using the theme directly
  const closeButtonStyle = {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  };

  return (
    <Dialog maxWidth={"xl"} fullWidth onClose={handleClose} aria-labelledby="add-recipe" open={open}>
      <DialogTitle
        id="add-recipe"
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2, 3),
        }}
      >
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          🍽️ Add New Recipe
        </Typography>
        <IconButton aria-label="close" sx={closeButtonStyle} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box p={3}>
        <Grid container spacing={4}>
          {/* --- Column 1: Food Definition --- */}
          <Grid item xs={12} md={4}>
            <Box component="form" noValidate autoComplete="off" sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Recipe Details
              </Typography>
              <TextField
                label="Recipe Title"
                placeholder="e.g., Spicy Chicken Tacos"
                onChange={handleTitleChange}
                value={title}
                sx={inputStyle}
              />
              <TextField
                label="Description"
                placeholder="A short summary of the dish"
                onChange={handleDescriptionChange}
                value={description}
                multiline
                rows={3}
                sx={inputStyle}
              />
              <TextField
                label="Servings"
                type="number"
                placeholder="e.g., 4"
                onChange={handleServingsChange}
                value={servings}
                sx={inputStyle}
              />
              <TextField
                label="Prep Time (mins)"
                type="number"
                placeholder="e.g., 15"
                onChange={handlePrepTimeChange}
                value={prepTime}
                sx={inputStyle}
              />
              <TextField
                label="Cook Time (mins)"
                type="number"
                placeholder="e.g., 45"
                onChange={handleCookTimeChange}
                value={cookTime}
                sx={inputStyle}
              />
              <TextField
                label="Image URL (Optional)"
                placeholder="Paste external image link here"
                onChange={handleImageChange}
                value={image}
                sx={inputStyle}
              />
            </Box>
          </Grid>

          {/* --- Column 2: Ingredients --- */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="secondary">
                Ingredients List
              </Typography>
              <Box sx={{ display: 'flex', gap: theme.spacing(1), mb: 2 }}>
                <TextField
                  label="Name"
                  placeholder="e.g., Tomato"
                  onChange={handleNameChange}
                  value={name}
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
                <TextField
                  label="Amount"
                  type="number"
                  inputProps={{ step: 0.1 }}
                  onChange={handleAmountChange}
                  value={amount}
                  size="small"
                  sx={{ width: '80px' }}
                />
                <TextField
                  label="Unit"
                  placeholder="e.g., oz / cup"
                  onChange={handleMeasurementChange}
                  value={measurement}
                  size="small"
                  sx={{ width: '100px' }}
                />
              </Box>
              <Button onClick={addIngredient} variant="contained" color="secondary" fullWidth disabled={!name}>
                Add Ingredient
              </Button>
            </Box>

            <Box mt={3} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Current Ingredients ({ingredients.length})
              </Typography>
              {ingredients.length === 0 ? (
                <Typography color="textSecondary">Add ingredients above to start your list.</Typography>
              ) : (
                <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing(2) }}>
                  {ingredients.map((item, index) => (
                    <li key={index} style={{ marginBottom: theme.spacing(0.5) }}>
                      <Typography variant="body1">
                        **{item.name}**: {item.amount} {item.measurement}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </Box>
          </Grid>

          {/* --- Column 3: Directions --- */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="info">
                Cooking Directions
              </Typography>
              <TextField
                label="Instruction Step"
                placeholder="e.g., Preheat oven to 350°F."
                onChange={handleInstructionChange}
                value={instruction}
                multiline
                rows={3}
                sx={inputStyle}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="optional-label">Optional Step?</InputLabel>
                <Select
                  labelId="optional-label"
                  label="Optional Step?"
                  value={optional}
                  onChange={handleOptionsChange}
                >
                  <MenuItem value={""}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
              <Button onClick={addDirection} variant="contained" color="info" fullWidth disabled={!instruction}>
                Add Direction
              </Button>
            </Box>

            <Box mt={3} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Current Directions ({directions.length})
              </Typography>
              {directions.length === 0 ? (
                <Typography color="textSecondary">Add steps above to create your directions list.</Typography>
              ) : (
                <ol style={{ paddingLeft: theme.spacing(2) }}>
                  {directions.map((item, index) => (
                    <li key={index} style={{ marginBottom: theme.spacing(1) }}>
                      <Typography variant="body1">
                        {item.instruction} ({item.optional})
                      </Typography>
                    </li>
                  ))}
                </ol>
              )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* --- Submission Button --- */}
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            onClick={addFoodItem}
            size="large"
            variant="contained"
            color="primary"
            sx={{ padding: theme.spacing(1.5, 6), fontSize: '1.1rem' }}
          >
            Submit New Recipe
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
export default AddRecipe;